import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import api from '../../services/api';
import { telemetryApi } from '../../services/telemetryApi';
import type { AircraftType } from '../../types';
import type { AircraftFlightInfoMap, DashboardAirstrip } from './types';

interface UseDashboardMapParams {
  liveAircrafts: AircraftType[];
  aircraftFlightInfo: AircraftFlightInfoMap;
  isValidCoordinate: (lat: number | null | undefined, lng: number | null | undefined) => boolean;
}

export const useDashboardMap = ({ liveAircrafts, aircraftFlightInfo, isValidCoordinate }: UseDashboardMapParams) => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapAirstrips, setMapAirstrips] = useState<DashboardAirstrip[]>([]);
  const [mapUpdateTrigger, setMapUpdateTrigger] = useState(0);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const routeLineSourcesRef = useRef<Set<string>>(new Set());
  const flightPathSourcesRef = useRef<Record<string, maplibregl.GeoJSONSource>>({});
  const flightPathDataRef = useRef<Record<string, Array<{ lat: number; lng: number }>>>({});

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: {
        version: 8,
        sources: {
          'google-satellite': {
            type: 'raster',
            tiles: [
              'https://mt0.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}',
              'https://mt1.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}',
              'https://mt2.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}',
              'https://mt3.google.com/vt/lyrs=s&hl=en&x={x}&y={y}&z={z}',
            ],
            tileSize: 256,
            maxzoom: 21,
          },
          'google-labels': {
            type: 'raster',
            tiles: [
              'https://mt0.google.com/vt/lyrs=h&hl=en&x={x}&y={y}&z={z}',
              'https://mt1.google.com/vt/lyrs=h&hl=en&x={x}&y={y}&z={z}',
              'https://mt2.google.com/vt/lyrs=h&hl=en&x={x}&y={y}&z={z}',
              'https://mt3.google.com/vt/lyrs=h&hl=en&x={x}&y={y}&z={z}',
            ],
            tileSize: 256,
            maxzoom: 21,
          },
        },
        layers: [
          { id: 'google-satellite', type: 'raster', source: 'google-satellite', minzoom: 0, maxzoom: 21 },
          { id: 'google-labels', type: 'raster', source: 'google-labels', minzoom: 0, maxzoom: 21 },
        ],
      },
      center: [36.633, -3.367],
      zoom: 13,
      pitch: 0,
      attributionControl: false,
    });

    map.on('load', () => {
      setMapLoaded(true);
      api.get('/airstrips').then((res) => {
        const data: any[] = res.data?.data || [];
        setMapAirstrips(
          data
            .filter((a) => a.latitude && a.longitude)
            .map((a) => ({
              code: a.code,
              icao: a.icao,
              name: a.name,
              lat: a.latitude,
              lng: a.longitude,
              elevationFt: a.elevationFt,
              runway: a.runway,
              runwayLengthM: a.runwayLengthM,
              runwayLengthFt: a.runwayLengthFt,
              runwayWidthFt: a.runwayWidthFt,
              surfaceType: a.surfaceType,
              surfaceLabel: a.surfaceLabel,
              lighted: a.lighted,
              fuelAvailable: a.fuelAvailable,
              landingFee: a.landingFee,
              handlingFee: a.handlingFee,
              country: a.country,
              dataSource: a.dataSource,
            }))
        );
      }).catch(() => {});
    });

    map.on('move', () => setMapUpdateTrigger((prev) => prev + 1));
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      flightPathSourcesRef.current = {};
    };
  }, []);

  const projectedPositions = useMemo(() => {
    const map = mapRef.current;
    if (!map) return {} as Record<string, { x: number; y: number } | null>;
    const result: Record<string, { x: number; y: number } | null> = {};
    liveAircrafts.forEach((ac) => {
      if (!ac.telemetry || !isValidCoordinate(ac.telemetry.lat, ac.telemetry.lng)) {
        result[ac.id] = null;
        return;
      }
      try {
        const p = map.project([ac.telemetry.lng, ac.telemetry.lat]);
        result[ac.id] = { x: p.x, y: p.y };
      } catch {
        result[ac.id] = null;
      }
    });
    return result;
  }, [liveAircrafts, mapUpdateTrigger, isValidCoordinate]);

  const projectedAirstrips = useMemo(() => {
    const map = mapRef.current;
    if (!map) return {} as Record<string, { x: number; y: number }>;
    const result: Record<string, { x: number; y: number }> = {};
    mapAirstrips.forEach((a) => {
      try {
        const p = map.project([a.lng, a.lat]);
        result[a.code] = { x: p.x, y: p.y };
      } catch {
        // skip invalid coords
      }
    });
    return result;
  }, [mapAirstrips, mapUpdateTrigger]);

  useEffect(() => {
    if (!mapRef.current || !mapRef.current.isStyleLoaded()) return;

    let isMounted = true;

    liveAircrafts.forEach((ac) => {
      if (!ac.tailNumber || !ac.telemetry) {
        const sourceId = `flight-path-${ac.tailNumber}`;
        const source = mapRef.current?.getSource(sourceId) as maplibregl.GeoJSONSource;
        if (source) {
          source.setData({ type: 'FeatureCollection', features: [] });
        }
        return;
      }

      const fetchAndUpdatePath = async () => {
        try {
          const history = await Promise.race([
            telemetryApi.getHistory(ac.tailNumber!, 2, 500).catch(() => []),
            new Promise<any[]>((resolve) => setTimeout(() => resolve([]), 4000)),
          ]);

          if (!isMounted) return;
          if (history.length < 2) {
            flightPathDataRef.current[ac.tailNumber] = [];
            return;
          }

          const map = mapRef.current;
          if (!map || !map.isStyleLoaded() || !isMounted) return;

          const validHistory = history.filter((log: any) => log.lat && log.lng && !isNaN(log.lat) && !isNaN(log.lng));
          const sortedHistory = [...validHistory].reverse();

          if (sortedHistory.length < 2) {
            flightPathDataRef.current[ac.tailNumber] = [];
            return;
          }

          flightPathDataRef.current[ac.tailNumber] = sortedHistory.map((log: any) => ({ lat: log.lat, lng: log.lng }));

          const sourceId = `flight-path-${ac.tailNumber}`;
          let source = map.getSource(sourceId) as maplibregl.GeoJSONSource;

          if (!source) {
            map.addSource(sourceId, {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] },
            });
            source = map.getSource(sourceId) as maplibregl.GeoJSONSource;

            map.addLayer({
              id: `flight-path-glow-${ac.tailNumber}`,
              type: 'line',
              source: sourceId,
              paint: { 'line-color': '#3b82f6', 'line-width': 8, 'line-opacity': 0.15, 'line-blur': 4 },
            });
            map.addLayer({
              id: `flight-path-line-${ac.tailNumber}`,
              type: 'line',
              source: sourceId,
              paint: { 'line-color': '#3b82f6', 'line-width': 3, 'line-opacity': 0.85 },
            });
          }

          const coordinates = sortedHistory.map((log: any) => [log.lng, log.lat] as [number, number]);

          if (isMounted && source) {
            source.setData({
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  geometry: { type: 'LineString', coordinates },
                  properties: {},
                },
              ],
            });
          }
        } catch {
          if (isMounted) {
            flightPathDataRef.current[ac.tailNumber] = [];
          }
        }
      };

      fetchAndUpdatePath();
    });

    return () => {
      isMounted = false;
    };
  }, [liveAircrafts, mapUpdateTrigger]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded() || mapAirstrips.length === 0) return;

    const airstripByCode: Record<string, { lat: number; lng: number }> = {};
    mapAirstrips.forEach((a) => {
      airstripByCode[a.code] = { lat: a.lat, lng: a.lng };
      if (a.icao) airstripByCode[a.icao] = { lat: a.lat, lng: a.lng };
    });

    liveAircrafts.forEach((ac) => {
      if (!ac.telemetry) return;
      const sourceId = `route-line-${ac.tailNumber}`;
      const layerGlow = `route-glow-${ac.tailNumber}`;
      const layerDash = `route-dash-${ac.tailNumber}`;

      const speed = ac.telemetry.groundSpeed || 0;
      const isFlying = speed > 30;
      const destCode = aircraftFlightInfo[ac.tailNumber]?.destination;
      const destPos = destCode ? airstripByCode[destCode] : null;

      if (!routeLineSourcesRef.current.has(sourceId)) {
        try {
          map.addSource(sourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
          map.addLayer({ id: layerGlow, type: 'line', source: sourceId, paint: { 'line-color': '#f59e0b', 'line-width': 5, 'line-opacity': 0.08, 'line-blur': 3 } });
          map.addLayer({ id: layerDash, type: 'line', source: sourceId, paint: { 'line-color': '#f59e0b', 'line-width': 1.2, 'line-opacity': 0.5, 'line-dasharray': [4, 5] } });
          routeLineSourcesRef.current.add(sourceId);
        } catch {
          return;
        }
      }

      const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
      if (!src) return;

      if (!isFlying || !destPos || !isValidCoordinate(ac.telemetry.lat, ac.telemetry.lng)) {
        src.setData({ type: 'FeatureCollection', features: [] });
        return;
      }

      const coords: [number, number][] = [
        [ac.telemetry.lng, ac.telemetry.lat],
        [destPos.lng, destPos.lat],
      ];
      src.setData({
        type: 'FeatureCollection',
        features: [{ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} }],
      });
    });
  }, [liveAircrafts, aircraftFlightInfo, mapAirstrips, mapLoaded, isValidCoordinate]);

  return {
    mapLoaded,
    mapAirstrips,
    mapUpdateTrigger,
    mapContainerRef,
    mapRef,
    projectedPositions,
    projectedAirstrips,
  };
};
